import { cloneElement, forwardRef, type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { HStack, Spinner, Text, VStack } from '@direct-frontend/components';
import { type OrderPaymentMethod, OrderPaymentMethods } from '@/shared/api/order';
import { usePayboxAnimation } from './lib/usePayboxAnimation';
import { usePayboxLogError } from './lib/usePayboxLogError';
import { usePayboxMessages } from './lib/usePayboxMessages';
import { usePayboxSrc } from './lib/usePayboxSrc';
import { YandexPayBadge } from '../YandexPayBadge';
import { type OrderPayboxSubmitParams } from './type';
import styles from './OrderFormYandexPayWidget.module.css';

interface OrderFormYandexPayWidgetProps {
    fallback: ReactElement<HTMLElement>;
    orderId: string;
    merchantId?: string;
    storeName: string;
    legalUrl: string;
    amount: number;
    nativePayEnabled: boolean;
    paymentMethods: string;
    paymentMethod: OrderPaymentMethod;
    loggedIn: boolean;
    onSubmit: (params: OrderPayboxSubmitParams) => void;
    setYaPayDiscount?: (discount?: string) => void;
}

export const OrderFormYandexPayWidget = forwardRef<HTMLElement, OrderFormYandexPayWidgetProps>(
    function OrderFormYandexPayWidget(props, forwardedRef) {
        const {
            fallback,
            orderId,
            merchantId,
            amount,
            storeName,
            legalUrl,
            nativePayEnabled,
            paymentMethods,
            paymentMethod,
            onSubmit,
            loggedIn,
            setYaPayDiscount,
        } = props;

        const iframeRef = useRef<HTMLIFrameElement>(null);
        const [payboxLoadError, setPayboxLoadError] = useState(false);

        const { payboxSrc, payboxHost } = usePayboxSrc({
            orderId,
            merchantId,
            storeName,
            legalUrl,
            amount,
            paymentMethods,
        });

        const { payboxLoaded, payboxHeight } = usePayboxMessages({
            iframeRef,
            amount,
            onSubmit,
            setYaPayDiscount,
        });

        const { logWidgetError } = usePayboxLogError({
            isPayboxEnabled: nativePayEnabled,
            isPayboxReady: payboxLoaded,
            isPayboxResized: payboxHeight !== 0,
        });

        const onErrorLoadPaybox = useCallback(() => {
            setPayboxLoadError(true);
            logWidgetError({ isIframeLoadError: true });
        }, [logWidgetError]);

        useEffect(() => {
            const iframe = iframeRef.current;

            iframe?.addEventListener('error', onErrorLoadPaybox);

            return () => {
                iframe?.removeEventListener('error', onErrorLoadPaybox);
            };
        }, [onErrorLoadPaybox]);

        usePayboxAnimation({
            iframeRef,
            nativePayEnabled,
            payboxSrc,
        });

        if (!payboxSrc && nativePayEnabled && payboxHost) {
            return (
                <HStack justifyContent="center">
                    <Spinner />
                </HStack>
            );
        }

        return payboxLoadError ? (
            cloneElement(fallback)
        ) : (
            <>
                {!payboxLoaded && nativePayEnabled && (
                    <HStack justifyContent="center" className="hidden-up-sm">
                        <Spinner />
                    </HStack>
                )}
                <VStack ref={forwardedRef} gap="12">
                    <iframe
                        height={payboxLoaded && nativePayEnabled ? payboxHeight : 0}
                        ref={iframeRef}
                        className={cn(styles.root, {
                            [styles.hidden]: !nativePayEnabled,
                        })}
                        src={payboxSrc}
                    />
                    {loggedIn && nativePayEnabled && paymentMethod === OrderPaymentMethods.CARD_ONLINE && (
                        <HStack gap="8" alignItems="center" justifyContent="center">
                            <Text size="m">Безопасная оплата с</Text>
                            <YandexPayBadge />
                        </HStack>
                    )}
                </VStack>
            </>
        );
    }
);
